import { useEffect, useMemo, useRef } from 'react';

import { format, parse, subDays } from 'date-fns';
import { isArray, isFunction } from 'lodash-es';

import { AppointmentSearch } from '@/features/book-appointment/booking.types';
import { NeuDatePicker } from '@hcahealthcaredpx/neutron-react';

import { HOLD_API_DATE_FORMAT, NEUTRON_DATEPICKER_FORMAT } from '../constants';

export const DatePicker = ({
  search,
  onDateSelect,
  onDateSelectCallback,
  onMonthChange,
}: {
  search: AppointmentSearch[];
  onDateSelect: (date: string) => void;
  onDateSelectCallback?: () => void;
  onMonthChange?: (displayedMonth: Date) => void;
}): JSX.Element => {
  // todo: set max to last day of 3 x 62 days in future
  // get the list of all disabled dates and pass it to the component
  // in the required format as an array
  // is this component going to work for us ?

  const disabledDates = useMemo(() => {
    return search.reduce((acc, current) => {
      if (isArray(current.timeSlots)) {
        return acc;
      }
      const parsedDate = parse(current.day, HOLD_API_DATE_FORMAT, new Date());
      const formattedDate = format(parsedDate, NEUTRON_DATEPICKER_FORMAT);

      return [formattedDate, ...acc];
    }, []);
  }, [search]);

  const yesterday = useMemo(() => format(subDays(new Date(), 1), NEUTRON_DATEPICKER_FORMAT), []);
  const lastAvailableDay = useMemo(() => {
    if (isArray(search) && search.length > 0) {
      const lastDay = search[search.length - 1].day;
      return format(parse(lastDay, HOLD_API_DATE_FORMAT, new Date()), NEUTRON_DATEPICKER_FORMAT);
    }
    return undefined;
  }, [search]);

  const calRef = useRef<HTMLDivElement>(null);

  /**
   * Parses the month/year text from the calendar header and returns a Date object
   * @param monthYearText - Text in format "MMMM yyyy" (e.g., "January 2025")
   * @returns Date object or null if parsing fails
   */
  const parseMonthFromHeader = (monthYearText: string | null | undefined): Date | null => {
    if (!monthYearText) return null;

    try {
      const displayedDate = parse(monthYearText.trim(), 'MMMM yyyy', new Date());
      return !isNaN(displayedDate.getTime()) ? displayedDate : null;
    } catch (error) {
      console.error('Error parsing month:', error);
      return null;
    }
  };

  // Monitor for month changes in the calendar header using MutationObserver
  // Todo: raise a github modernization board issue for Neutron team so
  // that this missing functionality is added to NeuDatePicker,
  // as its current form is very limiting for OAS.
  // Remove this custom workaround afterwards and use datepicker props.
  useEffect(() => {
    if (!calRef.current || !onMonthChange) return;

    const observer = new MutationObserver(() => {
      const monthYearText = calRef.current?.querySelector('.label div')?.textContent;
      const displayedDate = parseMonthFromHeader(monthYearText);
      if (displayedDate) {
        onMonthChange(displayedDate);
      }
    });

    // Observe changes to the calendar header
    const headerElement = calRef.current.querySelector('.label div');
    if (headerElement) {
      observer.observe(headerElement, { childList: true, subtree: true, characterData: true });

      // Set initial month
      const displayedDate = parseMonthFromHeader(headerElement.textContent);
      if (displayedDate) {
        onMonthChange(displayedDate);
      }
    }

    return () => observer.disconnect();
  }, [onMonthChange]);

  return (
    <div ref={calRef}>
      <NeuDatePicker
        label=""
        labelHidden
        today
        min={yesterday}
        max={lastAvailableDay}
        disabledDates={disabledDates || []}
        onNeuChange={(e) => {
          const parsedDate = parse(e.value, NEUTRON_DATEPICKER_FORMAT, new Date());
          const isInthePast = parsedDate < new Date();

          if (!e.value || isInthePast) return;

          onDateSelect(e.value);
          if (isFunction(onDateSelectCallback)) onDateSelectCallback();
        }}
      />
    </div>
  );
};
